import { useState, useEffect } from 'react'
import { getVipProgress, requestVipAccess, type VipProgress } from '@/services/vipService'
import { getMailingBalance } from '@/services/mailingBalanceService'
import { VipStatusBadge } from './VipStatusBadge'
import { AddMailingBalanceModal } from './AddMailingBalanceModal'

/* ── Main Dashboard Component ───────────────────────────────────── */

export function VipDashboard() {
  const [progress, setProgress] = useState<VipProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [mailingBalance, setMailingBalance] = useState(0)
  const [showBalanceModal, setShowBalanceModal] = useState(false)
  const [showLearnMore, setShowLearnMore] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [requestMessage, setRequestMessage] = useState('')

  useEffect(() => {
    getVipProgress()
      .then(setProgress)
      .catch(() => setError(true))
      .finally(() => setLoading(false))

    getMailingBalance()
      .then(data => setMailingBalance(data.balance))
      .catch(() => {}) // Balance defaults to 0
  }, [])

  const handleRequestAccess = async () => {
    setRequesting(true)
    setRequestMessage('')
    try {
      const result = await requestVipAccess()
      if (result.success) {
        setRequestMessage('Your request has been submitted.')
        // Update local state so button reflects cooldown
        setProgress(prev => prev ? { ...prev, can_request: false, last_request_at: result.requested_at || new Date().toISOString(), next_request_at: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString() } : prev)
      } else if (result.cooldown) {
        setProgress(prev => prev ? { ...prev, can_request: false, next_request_at: result.next_request_at || null } : prev)
      }
    } catch {
      setRequestMessage('Something went wrong. Please try again.')
    } finally {
      setRequesting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" />
      </div>
    )
  }

  if (error || !progress) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-[#87CEEB]">Unable to load your status. Please try again later.</p>
      </div>
    )
  }

  const isVip = progress.status === 'vip'

  // Format cooldown remaining
  const getCooldownText = () => {
    if (!progress.next_request_at) return null
    const next = new Date(progress.next_request_at)
    const now = new Date()
    const hoursLeft = Math.max(0, Math.ceil((next.getTime() - now.getTime()) / (1000 * 60 * 60)))
    if (hoursLeft <= 0) return null
    if (hoursLeft < 24) return `You can request again in ${hoursLeft} hour${hoursLeft === 1 ? '' : 's'}`
    const daysLeft = Math.ceil(hoursLeft / 24)
    return `You can request again in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`
  }

  return (
    <div className="space-y-3">
      {/* Current Status */}
      <VipStatusBadge status={progress.status} />

      {/* VIP Content */}
      {isVip ? (
        <div className="bg-white/70 rounded-2xl p-5 text-center">
          <p className="text-sm text-gray-600 leading-relaxed">
            You are one of our most valued members. Thank you for your continued support.
          </p>
        </div>
      ) : (
        /* Member Content - Request Access */
        <div className="bg-white/70 rounded-2xl p-5">
          <h3 className="text-lg font-bold text-gray-600 text-center mb-3">
            Status
          </h3>
          <p className="text-sm text-gray-500 text-center mb-4 leading-relaxed">
            VIP status is reserved for our most dedicated supporters.
          </p>

          {progress.can_request ? (
            <button
              onClick={handleRequestAccess}
              disabled={requesting}
              className="w-full py-3 bg-[#4AA3CC] text-white font-semibold rounded-xl hover:bg-[#3B8DB3] transition-colors disabled:opacity-50"
            >
              {requesting ? 'Submitting...' : 'Request VIP Access'}
            </button>
          ) : (
            <div className="text-center">
              <button
                disabled
                className="w-full py-3 bg-gray-200 text-gray-400 font-semibold rounded-xl cursor-not-allowed"
              >
                Request Submitted
              </button>
              {getCooldownText() && (
                <p className="text-xs text-gray-400 mt-2">{getCooldownText()}</p>
              )}
            </div>
          )}

          {requestMessage && (
            <p className="text-xs text-center text-gray-500 mt-2">{requestMessage}</p>
          )}
        </div>
      )}

      {/* Mailing Balance Card (shown for all) */}
      <button
        onClick={() => setShowBalanceModal(true)}
        className="w-full bg-white/70 rounded-2xl p-4 text-left hover:bg-white/90 transition-colors"
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-gray-400 font-medium">Mailing Balance</span>
          <span className="text-xl font-bold text-[#4AA3CC]">${mailingBalance.toFixed(2)}</span>
        </div>
        <p className="text-[11px] text-gray-400 leading-relaxed">
          Must have a minimum of $25 mailing balance and VIP status to receive the next shipment.
        </p>
        <div className="mt-2 text-center">
          <span className="text-xs font-semibold text-[#4AA3CC]">Tap to add balance</span>
        </div>
      </button>

      {/* Learn More Section */}
      <div className="bg-white/60 rounded-2xl p-4">
        <button
          onClick={() => setShowLearnMore(!showLearnMore)}
          className="w-full flex items-center justify-between"
        >
          <span className="text-sm font-semibold text-gray-500">learn more about vip</span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${showLearnMore ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showLearnMore && (
          <div className="mt-3 pt-3 border-t border-[#D4EEFF] text-sm text-gray-700 space-y-6 leading-relaxed">
            {/* Welcome */}
            <div className="space-y-2">
              <h4 className="text-base font-bold text-gray-900">welcome to the status program</h4>
              <p>this page is here to help you understand how the program works and why it exists in the first place.</p>
              <p>i wanted to create something real this year. something physical. something you can actually hold in your hands that proves you were here with me. this is about rewarding the people who stay close, who pay attention, and who continue to show up.</p>
              <p>some gifts may be simple, like a postcard from somewhere i just traveled. others may be much more meaningful. the value is not just the item itself. it is what it represents.</p>
            </div>

            {/* What is VIP */}
            <div className="space-y-2">
              <h4 className="text-base font-bold text-gray-900">what is vip</h4>
              <p>vip is the highest level inside Mini's World. it is not designed for everyone. it is meant for the few who are truly attentive, consistent, and in tune with me.</p>
              <p>this list will always stay small. those who earn it receive the best of me.</p>
            </div>

            {/* Shipping Balance */}
            <div className="space-y-2">
              <h4 className="text-base font-bold text-gray-900">shipping balance</h4>
              <p>shipping balance covers the cost of physical delivery only. maintaining a minimum of a $25.00 balance helps ensure packages are shipped quickly and smoothly.</p>
              <p>if your balance is below $25 when shipments go out your shipment may default to slower standard services.</p>
            </div>

            {/* Update Address */}
            <div className="space-y-2">
              <h4 className="text-base font-bold text-gray-900">need to update your address</h4>
              <p>
                click{' '}
                <a
                  href="/account"
                  onClick={(e) => {
                    e.preventDefault()
                    window.location.href = '/account'
                  }}
                  className="text-[#4AA3CC] font-bold underline underline-offset-2 hover:text-[#3B8DB3] transition-colors"
                >
                  here
                </a>{' '}
                to make changes.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add Balance Modal */}
      <AddMailingBalanceModal
        isOpen={showBalanceModal}
        currentBalance={mailingBalance}
        onClose={() => setShowBalanceModal(false)}
        onSuccess={(newBalance) => {
          setMailingBalance(newBalance)
          setShowBalanceModal(false)
        }}
      />
    </div>
  )
}
